import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Bike, Check, ChefHat, Clock, MessageCircle, ShoppingBag, XCircle } from 'lucide-react'
import { formatarReais, StatusPedido } from '@du/shared'
import { Badge, Button, Divider, Icone, InfoBand, SectionTitle, Skeleton } from '@du/ui'
import { api, BASE_API, ErroApi, type Loja } from '../lib/api.js'

type PedidoPublico = {
  /** Lido do lançamento, nunca recalculado na tela. Null até ser entregue. */
  pontosGanhos: number | null
  numeroDia: number
  status: StatusPedido
  tipo: string
  criadoEm: string
  tempoEstimadoMin: number | null
  prometidoPara: string | null
  subtotalCents: number
  taxaEntregaCents: number
  totalCents: number
  formaPagamento: string
  statusPagamento: string
  canceladoMotivo: string | null
  itens: { nome: string; quantidade: number; observacao: string | null; totalCents: number; adicionais: { nome: string }[] }[]
}

/** A linha do tempo que o cliente vê. Cancelado sai fora: é outro caminho. */
const ETAPAS = [
  { status: StatusPedido.RECEBIDO, rotulo: 'Pedido recebido', icone: Check },
  { status: StatusPedido.ACEITO, rotulo: 'Confirmado pela loja', icone: Check },
  { status: StatusPedido.EM_PREPARO, rotulo: 'Na chapa', icone: ChefHat },
  { status: StatusPedido.PRONTO, rotulo: 'Pronto', icone: ShoppingBag },
  { status: StatusPedido.SAIU_PARA_ENTREGA, rotulo: 'Saiu para entrega', icone: Bike },
  { status: StatusPedido.ENTREGUE, rotulo: 'Entregue', icone: Check },
]

type EstadoPix = {
  encontrado: boolean
  pixDisponivel?: boolean
  statusPagamento?: string
  pago?: boolean
  cobranca?: { status: string; qrCode: string | null; qrCodeBase64: string | null; expiraEm: string | null } | null
}

/**
 * O bloco de pagamento por Pix.
 *
 * Fica DENTRO do acompanhamento, não numa tela só dele: o cliente cai aqui
 * logo depois de pedir, e separar "pagar" de "acompanhar" em duas páginas
 * cria um passo a mais justamente no momento de maior desistência.
 *
 * Some sozinho quando o pagamento confirma — o evento `pedido.pagamento`
 * chega pelo SSE que esta página já escuta. Não há consulta em laço.
 */
function BlocoPix({ codigo, pago }: { codigo: string; pago: boolean }) {
  const [estado, setEstado] = useState<EstadoPix | null>(null)
  const [gerando, setGerando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)

  const carregar = useCallback(async () => {
    try {
      setEstado(await api<EstadoPix>(`/v1/publico/pedidos/${codigo}/pagamento`))
    } catch {
      // Silencioso de propósito: o pagamento é um extra desta tela, e o
      // acompanhamento do pedido não pode quebrar porque ele falhou.
    }
  }, [codigo])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const gerar = async () => {
    setGerando(true)
    setErro(null)
    try {
      const r = await api<{ qrCode: string | null; qrCodeBase64: string | null; expiraEm: string | null }>(
        `/v1/publico/pedidos/${codigo}/pix`,
        { method: 'POST' },
      )
      setEstado((e) => ({ ...(e ?? { encontrado: true }), cobranca: { status: 'PENDENTE', ...r } }))
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não deu para gerar o Pix agora.')
    } finally {
      setGerando(false)
    }
  }

  const copiar = async () => {
    const codigoPix = estado?.cobranca?.qrCode
    if (!codigoPix) return
    try {
      await navigator.clipboard.writeText(codigoPix)
      setCopiado(true)
      setTimeout(() => setCopiado(false), 2500)
    } catch {
      // Sem permissão de área de transferência (acontece em navegador antigo
      // e em http). O código fica visível na tela para copiar à mão.
      setErro('Copie o código manualmente abaixo.')
    }
  }

  if (pago) {
    return (
      <InfoBand tom="laranja">
        <Icone de={Check} tamanho={18} /> Pagamento confirmado. Obrigado!
      </InfoBand>
    )
  }
  if (estado && !estado.pixDisponivel) return null

  const cobranca = estado?.cobranca
  return (
    <section className="pix">
      <SectionTitle tamanho="sm">Pague com Pix</SectionTitle>
      {!cobranca?.qrCode ? (
        <>
          <p className="pix__ajuda">
            O pedido já está sendo preparado. O pagamento libera a saída da entrega.
          </p>
          <Button onClick={() => void gerar()} carregando={gerando}>
            Gerar código Pix
          </Button>
        </>
      ) : (
        <>
          {cobranca.qrCodeBase64 ? (
            <img
              className="pix__qr"
              src={`data:image/png;base64,${cobranca.qrCodeBase64}`}
              alt="QR Code do Pix. Se preferir, use o código copia e cola abaixo."
              width={220}
              height={220}
            />
          ) : null}
          <p className="pix__ajuda">
            Aponte a câmera do app do banco, ou use o <strong>copia e cola</strong>:
          </p>
          {/* readOnly e não disabled: campo desabilitado não deixa selecionar
              o texto, e selecionar à mão é o plano B de quem não tem
              permissão de área de transferência. */}
          <textarea className="pix__codigo" readOnly rows={3} value={cobranca.qrCode} />
          <Button onClick={() => void copiar()} variante={copiado ? 'secundario' : 'primario'}>
            {copiado ? 'Copiado!' : 'Copiar código'}
          </Button>
          <p className="pix__ajuda pix__ajuda--fraca">
            A confirmação aparece aqui sozinha, em alguns segundos. Não precisa
            mandar comprovante.
          </p>
        </>
      )}
      {erro ? (
        <InfoBand tom="amarelo">
          <Icone de={XCircle} tamanho={18} /> {erro}
        </InfoBand>
      ) : null}
    </section>
  )
}

export function Acompanhar({ loja }: { loja: Loja | null }) {
  const { codigo } = useParams<{ codigo: string }>()
  const navegar = useNavigate()
  const [pedido, setPedido] = useState<PedidoPublico | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [entrando, setEntrando] = useState(false)

  /**
   * Troca o código DESTA tela pela sessão e vai para a conta.
   *
   * Em um toque, sem senha e sem o sistema mandar mensagem nenhuma: quem está
   * aqui já tem o código, que é a credencial. Se a troca falhar, leva para a
   * conta assim mesmo — lá a tela explica como entrar, e um erro técnico no
   * meio do caminho não ajudaria ninguém.
   */
  const verMinhaConta = async () => {
    if (!codigo) return
    setEntrando(true)
    try {
      await api('/v1/eu/entrar', { method: 'POST', corpo: { codigo } })
    } catch {
      /* a tela da conta explica o que fazer */
    } finally {
      setEntrando(false)
      navegar('/minha-conta')
    }
  }

  const buscar = useCallback(async () => {
    if (!codigo) return
    try {
      setPedido(await api<PedidoPublico>(`/v1/publico/pedidos/${codigo}`))
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar seu pedido.')
    }
  }, [codigo])

  useEffect(() => {
    void buscar()
  }, [buscar])

  /**
   * Tempo real pelo mesmo SSE do portal, filtrado para este pedido.
   *
   * Sem isto o cliente ficaria recarregando a página para saber se o lanche
   * saiu — e é exatamente nesse momento que ele liga para a loja perguntar,
   * atrapalhando quem está na chapa.
   */
  useEffect(() => {
    if (!codigo) return
    const fonte = new EventSource(`${BASE_API}/v1/publico/pedidos/${codigo}/eventos`)
    const tratar = () => void buscar()
    for (const tipo of ['message', 'pedido.status', 'pedido.atualizado', 'pedido.pagamento']) {
      fonte.addEventListener(tipo, tratar)
    }
    return () => fonte.close()
  }, [codigo, buscar])

  if (erro) {
    return (
      <div className="bloco nada">
        <p>{erro}</p>
        <Link to="/">
          <Button variante="secundario">Voltar ao cardápio</Button>
        </Link>
      </div>
    )
  }

  if (!pedido) return <div className="bloco"><Skeleton altura={320} /></div>

  const cancelado = pedido.status === StatusPedido.CANCELADO
  const indiceAtual = ETAPAS.findIndex((e) => e.status === pedido.status)
  const visiveis = ETAPAS.filter(
    (e) => e.status !== StatusPedido.SAIU_PARA_ENTREGA || pedido.tipo === 'ENTREGA',
  )

  return (
    <div className="bloco acompanhar">
      <header className="acompanhar__topo">
        <span className="acompanhar__numero">#{String(pedido.numeroDia).padStart(3, '0')}</span>
      </header>

      {/* Antes dos itens de propósito: quem acabou de fechar o pedido vem
          para cá e precisa pagar, não conferir a lista de novo. */}
      {!cancelado && pedido.formaPagamento === 'PIX_ONLINE' ? (
        <BlocoPix codigo={codigo ?? ''} pago={pedido.statusPagamento === 'PAGO'} />
      ) : null}

      {cancelado ? (
        <div className="acompanhar__cancelado">
          <Icone de={XCircle} tamanho={48} />
          <h2>Pedido cancelado</h2>
          {pedido.canceladoMotivo ? <p>{pedido.canceladoMotivo}</p> : null}
        </div>
      ) : (
        <>
          <SectionTitle variante="laranja" tamanho="sm">
            {ETAPAS[indiceAtual]?.rotulo ?? 'Em andamento'}
          </SectionTitle>
          {pedido.prometidoPara && pedido.status !== StatusPedido.ENTREGUE ? (
            <p className="acompanhar__prazo">
              <Icone de={Clock} tamanho={18} />
              Previsão: {new Date(pedido.prometidoPara).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          ) : null}

          <ol className="trilha">
            {visiveis.map((e) => {
              const posicao = ETAPAS.findIndex((x) => x.status === e.status)
              const feito = posicao <= indiceAtual
              const atual = posicao === indiceAtual
              return (
                <li
                  key={e.status}
                  className={`trilha__etapa${feito ? ' trilha__etapa--feita' : ''}${atual ? ' trilha__etapa--atual' : ''}`}
                >
                  <span className="trilha__marca">
                    <Icone de={e.icone} tamanho={20} />
                  </span>
                  <span>{e.rotulo}</span>
                </li>
              )
            })}
          </ol>
        </>
      )}

      <Divider />

      <ul className="acompanhar__itens">
        {pedido.itens.map((i, n) => (
          <li key={n}>
            <span>
              {i.quantidade}× {i.nome}
              {i.adicionais.length > 0 ? (
                <em> {i.adicionais.map((a) => `+ ${a.nome}`).join(' ')}</em>
              ) : null}
              {i.observacao ? <em> · {i.observacao}</em> : null}
            </span>
            <span>{formatarReais(i.totalCents)}</span>
          </li>
        ))}
      </ul>

      <div className="resumo">
        {pedido.taxaEntregaCents > 0 ? (
          <div className="resumo__linha">
            <span>Taxa de entrega</span>
            <span>{formatarReais(pedido.taxaEntregaCents)}</span>
          </div>
        ) : null}
        <div className="resumo__linha resumo__linha--total">
          <span>Total</span>
          <strong>{formatarReais(pedido.totalCents)}</strong>
        </div>
        <p className="resumo__prazo">
          <Badge tom="neutro">{pedido.formaPagamento.replace(/_/g, ' ')}</Badge>
        </p>
      </div>

      {loja ? (
        <>
          <Button
            variante="pilula"
            icone={MessageCircle}
            larguraTotal
            onClick={() =>
              window.open(
                `https://wa.me/${loja.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá! Sobre meu pedido #${String(pedido.numeroDia).padStart(3, '0')}`)}`,
                '_blank',
                'noopener',
              )
            }
          >
            Falar sobre este pedido
          </Button>
          <InfoBand tom="preto">
            <span>Guarde este link para acompanhar</span>
          </InfoBand>

          {/*
            O convite, e ele é de graça: o pedido já é visível sem conta
            nenhuma, então a tela pode dizer os pontos sem autenticar ninguém.
            A pessoa descobre que eles existem porque RECEBEU, não porque leu
            um anúncio — e está parada, esperando o lanche.

            Nunca acima do Pix: enquanto houver código para pagar, é ele que
            manda na tela.
          */}
          {pedido.status !== StatusPedido.CANCELADO && loja.fidelidadeAtiva ? (
            <div className="fidelidade-convite">
              <p className="fidelidade-convite__frase">
                {pedido.pontosGanhos !== null
                  ? `Você somou ${pedido.pontosGanhos} ${pedido.pontosGanhos === 1 ? 'ponto' : 'pontos'} com este pedido.`
                  : 'Este pedido vai somar pontos quando for entregue.'}
              </p>
              <Button
                variante="secundario"
                larguraTotal
                carregando={entrando}
                onClick={() => void verMinhaConta()}
              >
                Ver meus pontos e pedidos
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      <Link to="/" className="voltar">
        Fazer outro pedido
      </Link>
    </div>
  )
}
